'use client'
import React, { useState, useEffect, useMemo, useContext, useRef } from 'react'
import { Select } from 'antd'
import { usePathname } from 'next/navigation'
import classNames from 'classnames'

import { getCountryKey } from 'helpers/country'
import { getCountryId } from 'helpers/country'
import { addEventTag, addParams } from 'helpers/utilities'
import { getValueById } from 'helpers/config/getValueById'
import { getLang, countryForCurrency } from 'helpers/country'
import {
  getSalaryOptions,
  getCountryList,
  getCurrencyList,
  initSalaryTypeLists
} from 'helpers/jobPayloadFormatter'

import {
  createUserPreferencesService,
  updateUserPreferencesService
} from 'store/services/users/addUserPreferences'

import Cascader from 'components/Cascader'
import { getUrlSearchString, jumpPage } from '../utils'

import JobFunction from '../JobFunction'
import LoadingButton from '../Common/LoadingButton'
import styles from './index.pc.module.scss'
import { ArrowIcon } from './icons'
import { useMainResumeContext } from '../Main/context'
import { removeEmptyOrNullValues } from 'helpers/formatter'
import { cloneDeep } from 'lodash-es'
import { trackAdjustEventBySite } from 'app/components/adjust'
import BannerCover from '../../images/jobseeker-cover.png'
import BannerCoverJP from '../../images/jobseeker-cover-jp.png'

import DesiredHeaderPc from '../Common/DesiredHeader.pc'
import { CustomRadio } from '../CustomRadio'
import Loading from 'app/components/loading'
import { fetchCheckFirstRegistration } from 'store/services/newIndex'
import { getCookie } from 'helpers/cookies'
import { RouterContext } from '../routerProvider'
import { ChangeCountryConfirmModal } from './component/ChangeCountryConfirmModal/index.pc'

const initType = {
  1: 'salary_ranges_lists',
  2: 'daily_salary_range_lists',
  3: 'hourly_salary_range_lists'
}

const formatData = (data, label, value) => {
  return data?.map((item) => ({
    label: item[label],
    value: item[value]
  }))
}

const DesiredJob = (props: { lang: any; config: any; initLoading?: boolean }) => {
  const { lang = {}, config } = props
  const profile = lang.profile || {}
  const { push } = useContext(RouterContext)
  const pathname = usePathname()
  const country = getCountryKey()
  const langKey = getLang()
  const { userInfo, getUserInfo } = useMainResumeContext()
  const isJp = country == 'jp'
  const isJobPreferences = useMemo(() => {
    if (userInfo?.is_job_preferences) {
      return true
    }
    return false
  }, [userInfo])
  const [salaryTypeLists, setSalaryTypeLists] = useState([])
  const [currencyLists] = useMemo(() => {
    return [getCurrencyList(config), getCountryList(config)]
  }, [config])
  // const salaryTypeLists = useMemo(() => {
  //   return initSalaryTypeLists(config?.salary_type_lists || []).filter((item) => item?.id != 4)
  // }, [config])
  const arrangementData = formatData(config.work_arrangement_lists, 'name', 'id')
  const jobTypeOptions = formatData(config.job_types, 'value', 'id')

  const [preference, setPreference] = useState(null)
  const [loading, setLoading] = useState(false)
  const [maxSalaryOptions, setMaxSalaryOptions] = useState([])
  const [needNextStep, setNeedNextStep] = useState(true)
  const localCurrency = countryForCurrency(country) || ''
  const [bindMinSalary, setBindMinSalary] = useState(0)
  const isNewUser = useRef(false)
  const [formData, setFormData] = useState({
    minSalary: null,
    maxSalary: null,
    workLocation: null,
    salaryCurrency: null,
    jobFunction: { id: undefined, value: '' },
    arrangement: 1,
    salaryType: null,
    jobTypeId: null
  })
  const [show, setShow] = useState(false)

  const disabledSubmit = useMemo(() => {
    const checkJobFunction = !formData.jobFunction.id
    if (checkJobFunction) return true

    if (!formData.arrangement) return true

    if (Number(formData.arrangement) != 3 && !formData.workLocation) return true

    if (Number(formData.arrangement) != 3 && formData.salaryType) {
      if (!formData.minSalary || !formData.maxSalary) return true
    }

    if (Number(formData.arrangement) == 3 && formData.salaryCurrency) {
      if (!formData.salaryType || !formData.minSalary || !formData.maxSalary) return true
    }

    if (!formData.jobTypeId) return true

    return false
  }, [formData])

  const getMaxSalaryOptions = (minSalary) => {
    if (!formData.salaryType) return []
    const salaryCurrency = formData.salaryCurrency || localCurrency
    const maxSalaryOptions = getSalaryOptions(
      config[initType[formData.salaryType]].find(
        (el) => el.currency_id === currencyLists.find((el) => el.key === salaryCurrency).id
      ),
      Number(minSalary),
      true
    )

    const maxSalaryOrg = maxSalaryOptions?.length > 0 ? maxSalaryOptions[0].value : null
    setFormData({ ...formData, maxSalary: maxSalaryOrg })
    setMaxSalaryOptions(maxSalaryOptions)
  }

  const minSalaryOptions = useMemo(() => {
    if (!formData.salaryType) return []
    const salaryCurrency = formData.salaryCurrency || localCurrency
    return getSalaryOptions(
      config[initType[formData.salaryType]].find(
        (el) => el.currency_id === currencyLists.find((el) => el.key === salaryCurrency)?.id
      )
    )
  }, [
    JSON.stringify(currencyLists),
    JSON.stringify(config.salary_ranges_lists),
    formData.salaryCurrency,
    formData.salaryType
  ])

  const getJobsPreferences = (userDetail) => {
    const _preferences = userDetail?.job_preferences
    if (!_preferences.length) return
    const _preference = _preferences[0] || {}
    setPreference(_preference)
    const {
      salary_range_from,
      salary_range_to,
      work_arrangement,
      currency_key,
      salary_type,
      daily_salary_range_from,
      daily_salary_range_to,
      hourly_salary_range_from,
      hourly_salary_range_to,
      function_job_title_id,
      job_type_id
    } = _preference

    const { id = '' } = salary_type || {}
    const from =
      id === 1 ? salary_range_from : id == 2 ? daily_salary_range_from : hourly_salary_range_from
    const to = id === 1 ? salary_range_to : id == 2 ? daily_salary_range_to : hourly_salary_range_to

    const _formData = {
      ...formData,
      minSalary: parseInt(from) || null,
      maxSalary: parseInt(to) || null,
      salaryType: isJobPreferences ? salary_type?.id : null,
      arrangement: work_arrangement?.id,
      salaryCurrency: isJobPreferences ? currency_key : null,
      jobTypeId: job_type_id || null,
      jobFunction: {
        id: function_job_title_id,
        value: getValueById(config, function_job_title_id, 'function_job_title_id') ?? ''
      }
    }

    if (work_arrangement?.id === 3) {
      _formData.salaryCurrency = currency_key
    }
    setFormData({ ...formData, ..._formData })
  }

  useEffect(() => {
    let _data = initSalaryTypeLists(config?.salary_type_lists || []).filter((item) => item?.id != 4)

    if (formData.jobTypeId == 1) {
      _data = _data.filter((item) => item?.value == 1)
    }
    if (_data) {
      setSalaryTypeLists(_data)
    }
  }, [config, formData.jobTypeId])

  useEffect(() => {
    addEventTag('new_user_direct_register')
    const eventTokens = {
      sg: 'gvmfx8',
      jp: '2vcvyj',
      ph: '7isidg',
      com: 'w0luou',
      id: '3n73t7'
    }
    trackAdjustEventBySite(eventTokens)
    fetchCheckFirstRegistration().then((res) => {
      isNewUser.current = res.data?.data.is_first_registration || false
    })
  }, [])

  useEffect(() => {
    if (userInfo?.id) {
      getJobsPreferences(userInfo)
      const { is_resume, is_educations, is_work_experiences, is_basic_info } = userInfo
      // 是否进入一下步骤
      setNeedNextStep(!(is_resume || (is_educations && is_work_experiences)) || !is_basic_info)
    }
  }, [userInfo])

  useEffect(() => {
    if (formData.minSalary) {
      getMaxSalaryOptions(formData.minSalary)
    }
  }, [formData.minSalary])
  useEffect(() => {
    if (bindMinSalary && minSalaryOptions?.length) {
      setBindMinSalary(0)
      setFormData((pre) => {
        return { ...pre, minSalary: minSalaryOptions[0].value }
      })
    }
  }, [bindMinSalary, minSalaryOptions])

  const getSalaryRangeKey = (salaryType) => {
    switch (salaryType) {
      case 1:
        return 'salary_range'
      case 2:
        return 'daily_salary_range'
      case 3:
        return 'hourly_salary_range'
      default:
        return ''
    }
  }

  const getSubmitData = () => {
    let funId = formData.jobFunction.id
    //
    if (funId?.includes?.('custom-')) {
      funId = parseInt(funId.replace('custom-', ''))
    }

    let params = {
      job_title: formData.jobFunction.value || '',
      function_job_title_id: funId, //formData.jobFunction.id,
      currency_key: formData.salaryCurrency,
      country_id: Number(formData.arrangement) < 3 ? getCountryId() : 241,
      work_arrangement_id: formData.arrangement || undefined,
      job_type_id: formData.jobTypeId || null
    }

    if (formData.salaryType) {
      params['salary_type_id'] = formData.salaryType
    }

    if (Number(formData.arrangement) == 3) {
      params['language_ids'] = '1'
    }

    if (Number(formData.arrangement) != 3) {
      params = addParams(params, formData.workLocation)
    }

    const salaryRangeKey = getSalaryRangeKey(formData.salaryType)
    if (salaryRangeKey) {
      params[`${salaryRangeKey}_from`] = Number(formData.minSalary)
      params[`${salaryRangeKey}_to`] = Number(formData.maxSalary)
    }

    const _params = cloneDeep(removeEmptyOrNullValues(params))

    if (!formData.salaryType && preference?.salary_type?.id) {
      const salaryRangeKey = getSalaryRangeKey(preference?.salary_type?.id)
      if (salaryRangeKey) {
        _params[`${salaryRangeKey}_from`] = null
        _params[`${salaryRangeKey}_to`] = null
        _params['salary_type_id'] = preference?.salary_type?.id
      }
    }

    return _params
  }

  const fetchA8 = () => {
    const countryKey = getCountryKey()
    const a8Key = '_A8Sales_'
    const a8Salas = getCookie(a8Key)
    // @ts-ignore
    if (window?.a8sales && countryKey == 'jp' && isNewUser.current && a8Salas) {
      // @ts-ignore
      window.a8sales?.({
        pid: 's00000026164001',
        order_number: `${userInfo?.id}-${new Date().getTime()}`,
        currency: 'JPY',
        items: [
          {
            code: 'a8dailytrial',
            price: 1,
            quantity: 1
          }
        ],
        total_price: 1
      })
      document.cookie =
        '_A8Sales_=; domain=.bossjob.jp; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
    }
  }

  const onContinue = () => {
    const params = getSubmitData()
    const searchWords = getUrlSearchString(2)
    setLoading(true)
    addEventTag('new_user_chat_preference_next')
    fetchA8()
    if (preference?.id) {
      updateUserPreferencesService({
        preferenceId: preference?.id,
        params
      })
        .then((res) => {
          if (res?.data) {
            if (needNextStep) {
              push(`${pathname}${searchWords}`)
            } else {
              jumpPage()
            }
          }
        })
        .finally(() => setLoading(false))
        .then(() => {
          getUserInfo()
        })
    } else {
      createUserPreferencesService({ params })
        .then((res) => {
          if (res.data) {
            if (needNextStep) {
              push(`${pathname}${searchWords}`)
            } else {
              jumpPage()
            }
          }
        })
        .finally(() => setLoading(false))
        .then(() => {
          getUserInfo()
        })
    }
  }

  return (
    <>
      <div className={classNames(styles.desired, 'yoloJobseekerCompletedSelect')}>
        <div className={styles.desiredContainer}>
          <div className={styles.leftBanner}>
            <div className={styles.leftBannerImage}>
              <img src={isJp ? BannerCoverJP.src : BannerCover.src} alt='' />
            </div>
          </div>

          {props?.initLoading ? (
            <div style={{ minHeight: 'calc(100vh - 180px)', position: 'relative', width: '100%' }}>
              <Loading />
            </div>
          ) : (
            <div className={styles.container}>
              <DesiredHeaderPc lang={lang} langKey={langKey} />
              <div className={styles.wrapper}>
                <div className={styles.content}>
                  <div className={styles.welcome}>{profile.desired?.wellcome}</div>
                  <div className={styles.form}>
                    <div className={styles.form_item}>
                      <div className={styles.form_label}>{profile?.JobTitleText}</div>
                      <div
                        className={classNames(
                          styles.form_value,
                          styles.modifyJobFunctionPlaceholder
                        )}
                      >
                        <JobFunction
                          lang={lang}
                          value={formData.jobFunction}
                          onChange={(value) => {
                            let newValue = { id: value.value || value.id, value: value.label }
                            setFormData({ ...formData, jobFunction: newValue })
                          }}
                        />
                      </div>
                    </div>

                    <div className={styles.form_item}>
                      <div className={styles.form_label}>{profile?.workArrangement}</div>
                      <div className={styles.form_value}>
                        <div className={styles.radioGroup}>
                          {arrangementData?.map((item) => (
                            <CustomRadio
                              checked={item.value == formData.arrangement}
                              onChange={(event) => {
                                let salaryCurrency = isJobPreferences ? localCurrency : null
                                let isRemote = Number(event.target.value) === 3

                                setFormData({
                                  ...formData,
                                  arrangement: event.target.value,
                                  minSalary: null,
                                  maxSalary: null,
                                  salaryType: null,
                                  salaryCurrency,
                                  // clear workLocation when user opt remote job
                                  workLocation: isRemote ? null : formData.workLocation
                                })
                              }}
                              label={item.label}
                              value={item.value}
                              key={item.value}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    {formData.arrangement != 3 && (
                      <div className={styles.form_item}>
                        <div className={styles.form_label}>{profile.desired?.workLocation}</div>
                        <div className={styles.form_value}>
                          <Cascader
                            placeholder={profile.desired?.workLocationPlaceholder}
                            preference={preference}
                            onChange={(location) => {
                              setTimeout(() => {
                                // console.log('location123', location)
                                setFormData({ ...formData, workLocation: location })
                              }, 0)
                            }}
                            suffixIcon={ArrowIcon}
                            showOtherCountry
                            onOtherCountryClick={() => {
                              setShow(true)
                            }}
                          />
                        </div>
                      </div>
                    )}

                    <div className={styles.form_item}>
                      <div className={styles.form_label}>{profile?.desiredJobType}</div>
                      <div className={styles.form_value + ' ' + styles.form_salary}>
                        <Select
                          style={{ height: '42px' }}
                          options={jobTypeOptions}
                          suffixIcon={<ArrowIcon />}
                          placeholder={profile.desired?.jobTypePlaceholder}
                          value={formData.jobTypeId}
                          className={styles.form_minSalary}
                          popupClassName='selectGlobalPopupClassName'
                          allowClear
                          onChange={(value) => {
                            setFormData({
                              ...formData,
                              jobTypeId: value,
                              salaryType: null
                            })
                          }}
                        />
                      </div>
                    </div>

                    <div className={styles.form_item}>
                      <div className={styles.form_label}>
                        {profile.desired?.salaryRange}{' '}
                        <span className={styles.form_optional}>({profile.optional})</span>
                      </div>
                      <div className={styles.form_value + ' ' + styles.form_salary}>
                        {formData.arrangement == 3 && (
                          <Select
                            style={{ height: '42px' }}
                            options={currencyLists}
                            suffixIcon={<ArrowIcon />}
                            placeholder={profile.desired?.salaryCurrencyPlaceholder}
                            value={formData.salaryCurrency}
                            className={styles.form_salaryType}
                            popupClassName='selectGlobalPopupClassName'
                            allowClear
                            onChange={(value) => {
                              setBindMinSalary(value ? Math.random() : 0)
                              setFormData({
                                ...formData,
                                salaryCurrency: value,
                                salaryType: value ? 1 : null,
                                minSalary: null,
                                maxSalary: null
                              })
                            }}
                          />
                        )}
                        {formData?.salaryCurrency || formData?.arrangement != 3 ? (
                          <Select
                            style={{ height: '42px' }}
                            options={salaryTypeLists}
                            disabled={!formData.salaryCurrency && formData.arrangement == 3}
                            suffixIcon={<ArrowIcon />}
                            placeholder={profile.desired?.salaryType}
                            className={styles.form_salaryType}
                            value={formData.salaryType}
                            popupClassName='selectGlobalPopupClassName'
                            allowClear
                            onChange={(value) => {
                              setBindMinSalary(Math.random())
                              setFormData({
                                ...formData,
                                salaryType: value,
                                minSalary: null,
                                maxSalary: null
                              })
                            }}
                          />
                        ) : null}
                        {formData?.salaryType ? (
                          <div className={styles.form_salaryRange}>
                            <Select
                              disabled={!minSalaryOptions?.length}
                              style={{ height: '42px' }}
                              options={minSalaryOptions}
                              suffixIcon={<ArrowIcon />}
                              placeholder={profile.desired?.salaryFrom}
                              className={styles.form_minSalary}
                              value={formData.minSalary}
                              popupClassName='selectGlobalPopupClassName'
                              onChange={(value) => {
                                setFormData({ ...formData, minSalary: value })
                              }}
                            />
                            <Select
                              disabled={
                                !maxSalaryOptions?.length ||
                                !maxSalaryOptions?.length ||
                                !formData.minSalary
                              }
                              style={{ height: '42px' }}
                              options={maxSalaryOptions}
                              popupClassName='selectGlobalPopupClassName'
                              suffixIcon={<ArrowIcon />}
                              placeholder={profile.desired?.salaryTo}
                              className={styles.form_maxSalary}
                              value={formData.maxSalary}
                              onChange={(value) => {
                                setFormData({ ...formData, maxSalary: value })
                              }}
                            />
                          </div>
                        ) : null}
                      </div>
                    </div>

                    {/* {chatProcess ? (
                      <div className={styles.form_submit_right}>
                        <LoadingButton
                          className={classNames(styles.form_submitBtn)}
                          loading={loading}
                          disabledSubmit={disabledSubmit || loading}
                          onSubmit={onContinue}
                          text={profile.continue}
                        />
                      </div>
                    ) : (
                      <div className={styles.form_submit_left}>
                        <LoadingButton
                          className={classNames(styles.form_submitBtn)}
                          loading={loading}
                          disabledSubmit={disabledSubmit || loading}
                          onSubmit={onSubmit}
                          text={needNextStep ? profile.desired?.button2 : profile.confirm}
                        />
                      </div>
                    )} */}

                    <div className={styles.form_submit_left}>
                      <LoadingButton
                        className={classNames(styles.form_submitBtn)}
                        loading={loading}
                        disabledSubmit={disabledSubmit || loading}
                        onSubmit={onContinue}
                        text={needNextStep ? profile?.next : profile?.confirm}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <ChangeCountryConfirmModal
        open={show}
        onClose={() => {
          setShow(false)
        }}
      />
    </>
  )
}

export default DesiredJob
